import { useStore } from '@nanostores/react';
import { useQueryClient } from '@tanstack/react-query';
import type { SettingsWorkspaceSettings } from '@customerio/hydra-ui-api';
import { useHydraUIAPIQuery } from '@/services/api';
import { isFlexPlan } from '@/services/auth/plan-slugs';
import { accountStore } from '@/services/auth/store';
import { requireWorkspaceId } from '@/services/db/shared/workspace';
import { getWorkspaceSettings, workspaceSettingsKeys } from './queries';

export const DATA_DRIVEN_SEGMENT_LIMIT_TITLE = 'Data-driven segment limit reached';

type SegmentQuota = Pick<SettingsWorkspaceSettings, 'data_driven_segments_used' | 'data_driven_segments_allotted'>;

/**
 * Whether the workspace has used every data-driven segment its plan allows.
 *
 * An allotment of 0 means no limit. Settings that do not report both counts
 * say nothing about a limit, so they do not block: the backend still refuses
 * a save over the limit, and guessing one here could block a save it would
 * have taken.
 */
export function isDataDrivenSegmentLimitReached(settings: SegmentQuota | undefined): boolean {
  const cap = dataDrivenSegmentCap(settings);
  return cap != null && cap.used >= cap.allotted;
}

function dataDrivenSegmentCap(settings: SegmentQuota | undefined): { used: number; allotted: number } | null {
  const used = settings?.data_driven_segments_used;
  const allotted = settings?.data_driven_segments_allotted;
  if (used == null || allotted == null || allotted <= 0) {
    return null;
  }
  return { used, allotted };
}

/**
 * Whether the current workspace can create no more data-driven segments, read
 * from its workspace settings.
 *
 * Anything that creates one (a new segment, or a form whose submissions join a
 * segment of their own) is refused at the limit, so a screen reads this to say
 * so before the user builds something they could not keep.
 */
export function useIsDataDrivenSegmentLimitReached(): boolean {
  return isDataDrivenSegmentLimitReached(useSegmentQuota());
}

/**
 * Whether the account should be offered the segments add-on: a Flex account
 * with a data-driven segment cap. Owning the add-on lifts the cap, which the
 * allotment reports as 0. Twin of ui's `offersDataDrivenSegmentsAddon`.
 */
export function offersDataDrivenSegmentsAddon(
  account: Parameters<typeof isFlexPlan>[0],
  settings: SegmentQuota | undefined,
): boolean {
  return dataDrivenSegmentCap(settings) != null && isFlexPlan(account);
}

export function useOffersDataDrivenSegmentsAddon(): boolean {
  const account = useStore(accountStore);
  return offersDataDrivenSegmentsAddon(account, useSegmentQuota());
}

/** The plan's data-driven segment allotment, nullish until the settings load. */
export function useDataDrivenSegmentsAllotted(): number | null | undefined {
  return useSegmentQuota()?.data_driven_segments_allotted;
}

/** Refetches the quota, so at-cap states clear once the add-on lifts the cap. */
export function useRefetchSegmentQuota(): () => Promise<void> {
  const queryClient = useQueryClient();
  const workspaceId = requireWorkspaceId();
  return () => queryClient.invalidateQueries({ queryKey: workspaceSettingsKeys.detail(workspaceId) });
}

function useSegmentQuota(): SegmentQuota | undefined {
  const workspaceId = requireWorkspaceId();
  const query = useHydraUIAPIQuery({
    queryKey: workspaceSettingsKeys.detail(workspaceId),
    queryFn: ({ signal }) => getWorkspaceSettings(workspaceId, signal),
    queryConfig: { staleTime: 0, refetchOnWindowFocus: false },
  });
  return query.data?.settings;
}
